import React, { useState, useRef, useEffect } from 'react';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import './DateRangePicker.css';

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];

const DateRangePicker = ({ label, startKey, endKey, formData, setFormData, noLabel = false, split = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selecting, setSelecting] = useState('start'); // 'start' | 'end'
  const [hoverDate, setHoverDate] = useState(null);
  const containerRef = useRef(null);

  const startDt = formData[startKey] || null;
  const endDt = formData[endKey] || null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${y}.${m}.${d}`;
  };

  const displayText = () => {
    if (startDt && endDt) return `${formatDate(startDt)} ~ ${formatDate(endDt)}`;
    if (startDt) return `${formatDate(startDt)} ~`;
    return '';
  };

  const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const handleDateClick = (dateStr) => {
    if (selecting === 'start') {
      setFormData(prev => ({ ...prev, [startKey]: dateStr, [endKey]: '' }));
      setSelecting('end');
    } else {
      if (dateStr < startDt) {
        setFormData(prev => ({ ...prev, [startKey]: dateStr, [endKey]: '' }));
        setSelecting('end');
      } else {
        setFormData(prev => ({ ...prev, [endKey]: dateStr }));
        setSelecting('start');
        setIsOpen(false);
      }
    }
  };

  const isInRange = (dateStr) => {
    if (selecting === 'end' && startDt && hoverDate) {
      const end = hoverDate > startDt ? hoverDate : startDt;
      const start = hoverDate > startDt ? startDt : hoverDate;
      return dateStr >= start && dateStr <= end;
    }
    if (startDt && endDt) {
      return dateStr >= startDt && dateStr <= endDt;
    }
    return false;
  };

  const isStart = (dateStr) => dateStr === startDt;
  const isEnd = (dateStr) => dateStr === endDt;

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const renderMonth = (monthDate) => {
    const daysInMonth = getDaysInMonth(monthDate);
    const firstDay = getFirstDayOfMonth(monthDate);
    const cells = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} className="drp-day-cell drp-empty" />);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(monthDate.getFullYear(), monthDate.getMonth(), d);
      const dateStr = toDateStr(date);
      const inRange = isInRange(dateStr);
      const start = isStart(dateStr);
      const end = isEnd(dateStr);

      cells.push(
        <div
          key={d}
          className={`drp-day-cell ${inRange ? 'drp-in-range' : ''} ${start ? 'drp-start' : ''} ${end ? 'drp-end' : ''}`}
          onClick={() => handleDateClick(dateStr)}
          onMouseEnter={() => setHoverDate(dateStr)}
        >
          {d}
        </div>
      );
    }

    const monthLabel = `${monthDate.getFullYear()}년 ${monthDate.getMonth() + 1}월`;

    return (
      <div className="drp-month">
        <div className="drp-month-label">{monthLabel}</div>
        <div className="drp-weekdays">
          {DAYS.map(day => <div key={day} className="drp-weekday">{day}</div>)}
        </div>
        <div className="drp-days">{cells}</div>
      </div>
    );
  };

  const nextMonthDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1);

  const handleClear = (e) => {
    e.stopPropagation();
    setFormData(prev => ({ ...prev, [startKey]: '', [endKey]: '' }));
    setSelecting('start');
  };

  const openForStart = () => {
    setSelecting('start');
    setIsOpen(true);
  };

  const openForEnd = () => {
    setSelecting(startDt ? 'end' : 'start');
    setIsOpen(true);
  };

  const calendarDropdown = isOpen && (
    <div className="drp-dropdown" onMouseLeave={() => setHoverDate(null)}>
      <div className="drp-header">
        <button type="button" className="drp-nav-btn" onClick={prevMonth}><FaChevronLeft /></button>
        <span className="drp-selecting-hint">
          {selecting === 'start' ? '시작일 선택' : '종료일 선택'}
        </span>
        <button type="button" className="drp-nav-btn" onClick={nextMonth}><FaChevronRight /></button>
      </div>
      <div className="drp-calendars">
        {renderMonth(currentMonth)}
        {renderMonth(nextMonthDate)}
      </div>
    </div>
  );

  // split 모드: 두 개의 분리된 인풋 + ~ 구분자
  if (split) {
    return (
      <div className="drp-container drp-split-container" ref={containerRef}>
        <div className="drp-split-row">
          <input
            type="text"
            readOnly
            value={startDt ? formatDate(startDt) : ''}
            placeholder="시작일"
            className={`drp-split-input ${selecting === 'start' && isOpen ? 'drp-split-active' : ''}`}
            onClick={openForStart}
          />
          <span className="drp-split-separator">~</span>
          <input
            type="text"
            readOnly
            value={endDt ? formatDate(endDt) : ''}
            placeholder="종료일"
            className={`drp-split-input ${selecting === 'end' && isOpen ? 'drp-split-active' : ''}`}
            onClick={openForEnd}
          />
          {(startDt || endDt) && (
            <span className="drp-split-clear" onClick={handleClear}>&times;</span>
          )}
        </div>
        {calendarDropdown}
      </div>
    );
  }

  // noLabel 모드 (기존)
  if (noLabel) {
    return (
      <div className="drp-container" ref={containerRef}>
        <div className="drp-inline-group" onClick={() => setIsOpen(!isOpen)}>
          <input
            type="text"
            readOnly
            value={displayText()}
            placeholder="기간 선택"
            className="drp-inline-input"
          />
          {(startDt || endDt) && (
            <span className="drp-clear" onClick={handleClear}>&times;</span>
          )}
        </div>
        {calendarDropdown}
      </div>
    );
  }

  // 기본 모드 (floating label)
  return (
    <div className="drp-container" ref={containerRef}>
      <div className="floating-label-group" onClick={() => setIsOpen(!isOpen)}>
        <input
          type="text"
          readOnly
          value={displayText()}
          placeholder=" "
          className="floating-input drp-input"
        />
        <label className="floating-label">{label}</label>
        {(startDt || endDt) && (
          <span className="drp-clear" onClick={handleClear}>&times;</span>
        )}
      </div>
      {calendarDropdown}
    </div>
  );
};

export default DateRangePicker;
